import React, { useCallback, useMemo } from "react";
import ScreenCard from "../ScreenCard";
import ListItem from "../../ui_atoms/ListItem";
import { useI18n } from "../../hooks/i18n-hooks";
import { SHORT_DATE_IN_LIST_FORMAT_OPTIONS, useDateFormatter } from "../../hooks/date";
import { useQuery } from "@apollo/client/react";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile, InvitationTarget } from "../../__generated__/graphql";
import {
    ClientUserFieldsFragment,
    InvitationFieldsFragment,
} from "../../__generated__/operations/graphql";
import Loading from "../../components/Loading";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import { StackScreenProps } from "@react-navigation/stack";
import { IconName } from "../../components/Icon";
import { useTheme } from "../../hooks/use-theme";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import { useTracking } from "../../hooks/tracking";

type RootParams = StackScreenProps<RootStackParamList>;
type ClientColleaguesScreenProps = StackScreenProps<ProfileStackParamList, "Colleagues">;

const ClientColleaguesScreen: React.FC<ClientColleaguesScreenProps> = ({ navigation }) => {
    const currentProfile = useCurrentProfile<ClientProfile>();
    const { data: { getClientUsers: clientUsers = [], clientInvitationsSent = [] } = {} } =
        useQuery(GET_CLIENT_USERS, {
            variables: {
                clientId: currentProfile?.client?.id || "",
            },
        });
    const i18n = useI18n();
    const { colors } = useTheme();
    const { trackEvent } = useTracking();

    const formatDate = useDateFormatter();

    const effectiveClientUsers = useMemo(() => {
        const unionArray: (ClientUserFieldsFragment | InvitationFieldsFragment)[] = [
            ...clientUsers,
            ...clientInvitationsSent,
        ];

        const nonSortedItems = unionArray.map((colleague) => {
            const isInvitation = "mode" in colleague;
            // An invitation sent by email (from the web app) has no phone number.
            let colleagueName = colleague.phoneNumber || (isInvitation && colleague.email) || "";
            if (colleague.firstName && colleague.lastName) {
                colleagueName = `${colleague.firstName} ${colleague.lastName}`;
            }
            if (colleague.id === currentProfile?.id) {
                colleagueName = i18n.t("screens.user.my_colleagues.you", {
                    name: colleagueName,
                });
            }
            return {
                tags: !isInvitation
                    ? [
                          i18n.t("screens.user.my_colleagues.joined_at", {
                              joinedAt: formatDate(
                                  (colleague as ClientUserFieldsFragment).joinedAt,
                                  SHORT_DATE_IN_LIST_FORMAT_OPTIONS,
                              ),
                          }),
                      ]
                    : [],
                id: colleague.id,
                value: colleague.id,
                title: colleagueName,
                ...(isInvitation
                    ? {
                          icon: "talent" as IconName,
                          iconColor: colors.onSurfaceDisabled,
                      }
                    : {
                          avatarPath: (colleague as ClientUserFieldsFragment).avatarUrl,
                      }),
                useBigAvatarOrIcon: true,
                labels: [
                    {
                        text: isInvitation ? i18n.t("client_worker_cell.invitation") : undefined,
                        mode: "warning" as const,
                    },
                    {
                        text:
                            !isInvitation &&
                            !(colleague as ClientUserFieldsFragment).canSignContracts
                                ? i18n.t("client_worker_cell.cannot_sign_contracts")
                                : undefined,
                        mode: "warning" as const,
                    },
                ],
                onPress: isInvitation
                    ? () => {
                          (navigation as RootParams["navigation"]).navigate("SendInvitation", {
                              target: InvitationTarget.Client,
                              invitationId: colleague.id,
                          });
                      }
                    : () =>
                          navigation.navigate("ColleagueDetails", {
                              colleagueId: colleague.id,
                          }),
            };
        });
        return nonSortedItems;
    }, [clientUsers, clientInvitationsSent, currentProfile, colors, i18n, navigation, formatDate]);

    const goToSendInvitation = useCallback(() => {
        void trackEvent("click_create_colleague_invitation");
        (navigation as RootParams["navigation"]).navigate("SendInvitation", {
            target: InvitationTarget.Client,
        });
    }, [navigation, trackEvent]);

    const dynamicButtonProps = useDynamicHeaderButton({
        title: i18n.t("screens.user.my_colleagues.invite_colleague"),
        onClick: goToSendInvitation,
    });
    return (
        <ScreenCard {...dynamicButtonProps}>
            {effectiveClientUsers ? (
                effectiveClientUsers.map((d, index) => <ListItem key={index} {...d} />)
            ) : (
                <Loading />
            )}
        </ScreenCard>
    );
};

export default ClientColleaguesScreen;
